import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { SEO } from "@/components/SEO";

const EMPTY = {
  name: "", title: "", slug: "", photo: "", bio: "", experience_years: 0, location: "",
  cities_served: [], services: [], starting_price: 0, rating: 5, featured: false, active: true,
  availability_status: "available", availability_note: "", portfolio: [],
};

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/artists").then((r) => setArtists(r.data)).catch(() => {});
    api.get("/admin/services").then((r) => setAllServices(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a, cities_served: a.cities_served || [], services: a.services || [], portfolio: a.portfolio || [] }); setOpen(true); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const toggleService = (name) =>
    setForm((f) => ({ ...f, services: f.services.includes(name) ? f.services.filter((s) => s !== name) : [...f.services, name] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, experience_years: Number(form.experience_years) || 0,
      starting_price: Number(form.starting_price) || 0, rating: Number(form.rating) || 5, slug: form.slug || undefined };
    try {
      if (editing) await api.put(`/admin/artists/${editing.id}`, payload);
      else await api.post("/admin/artists", payload);
      toast.success(editing ? "Artist updated" : "Artist added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/artists/${a.id}`);
      toast.success("Artist deleted");
      load();
    } catch { toast.error("Could not delete artist"); }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/artists/${a.id}`, { ...EMPTY, ...a, active: !a.active, slug: a.slug });
      load();
    } catch { toast.error("Could not update artist"); }
  };

  const inputCls = "rounded-xl bg-ivory/5 border-ivory/15 text-ivory h-11";

  return (
    <div data-testid="admin-artists-page">
      <SEO title="Manage Artists" description="The Velvet Brush admin." path="/admin/artists" />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">Artists</h1>
          <p className="mt-1 text-sm text-ivory/50">Add, edit and manage your makeup artists.</p>
        </div>
        <Button onClick={openNew} data-testid="add-artist-btn" className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add Artist
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {artists.map((a) => (
          <div key={a.id} data-testid={`admin-artist-card-${a.slug}`} className="bg-ivory/5 border border-ivory/10 rounded-2xl p-5 flex gap-4">
            <img src={a.photo} alt={a.name} className="w-20 h-24 rounded-xl object-cover object-top bg-ivory/10 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-xl leading-tight">{a.name}</h3>
                  <p className="text-xs text-ivory/50 mt-0.5">{a.location} • {a.experience_years}+ yrs • From {inr(a.starting_price)}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(a)} data-testid={`edit-artist-${a.slug}`} className="p-2 text-ivory/60 hover:text-velvet"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(a)} data-testid={`delete-artist-${a.slug}`} className="p-2 text-ivory/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${a.active ? "bg-sage/20 text-sage" : "bg-ivory/10 text-ivory/40"}`}>
                  {a.active ? "Active" : "Inactive"}
                </span>
                <label className="flex items-center gap-2 text-xs text-ivory/50">
                  <Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`toggle-active-${a.slug}`} />
                  {a.active ? "Live" : "Hidden"}
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#262525] text-ivory border-ivory/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="artist-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Artist" : "Add Artist"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <Label>Name *</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="artist-name-input" className={inputCls} />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={set("title")} data-testid="artist-title-input" className={inputCls} placeholder="e.g. Lead Makeup Artist" />
            </div>
            <div className="sm:col-span-2">
              <Label>Photo</Label>
              <div className="flex gap-2">
                <Input value={form.photo?.startsWith("data:") ? "" : form.photo} onChange={set("photo")} data-testid="artist-photo-input"
                  className={inputCls} placeholder="Image URL, or upload →" />
                <label className="shrink-0 cursor-pointer bg-ivory/10 hover:bg-ivory/20 rounded-xl px-4 flex items-center text-sm transition-colors" data-testid="artist-photo-upload-label">
                  Upload
                  <input type="file" accept="image/*" className="hidden" data-testid="artist-photo-upload" onChange={onPhotoFile} />
                </label>
              </div>
              {form.photo && <img src={form.photo} alt="preview" className="mt-2 h-24 rounded-xl object-cover" data-testid="artist-photo-preview" />}
            </div>
            <div className="sm:col-span-2">
              <Label>Bio</Label>
              <Textarea value={form.bio} onChange={set("bio")} data-testid="artist-bio-input" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory min-h-[90px]" />
            </div>
            <div>
              <Label>Experience (years)</Label>
              <Input type="number" min="0" value={form.experience_years} onChange={set("experience_years")} data-testid="artist-experience-input" className={inputCls} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={set("location")} data-testid="artist-location-input" className={inputCls} placeholder="Lucknow" />
            </div>
            <div>
              <Label>Starting Price (₹)</Label>
              <Input type="number" min="0" value={form.starting_price} onChange={set("starting_price")} data-testid="artist-price-input" className={inputCls} />
            </div>
            <div>
              <Label>Rating</Label>
              <Input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={set("rating")} data-testid="artist-rating-input" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <Label>Cities Served (comma separated)</Label>
              <Input value={form.cities_served.join(", ")} data-testid="artist-cities-input"
                onChange={(e) => setForm((f) => ({ ...f, cities_served: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
                className={inputCls} placeholder="Lucknow, Kanpur, Delhi NCR" />
            </div>
            <div className="sm:col-span-2">
              <Label>Services Offered</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {allServices.map((s) => (
                  <button type="button" key={s.id} onClick={() => toggleService(s.name)}
                    data-testid={`artist-service-toggle-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                      form.services.includes(s.name) ? "bg-velvet border-velvet text-ink" : "border-ivory/20 text-ivory/60 hover:border-velvet"
                    }`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Availability</Label>
              <Select value={form.availability_status} onValueChange={(v) => setForm((f) => ({ ...f, availability_status: v }))}>
                <SelectTrigger data-testid="artist-availability-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available" data-testid="avail-available">Available</SelectItem>
                  <SelectItem value="unavailable" data-testid="avail-unavailable">Unavailable</SelectItem>
                  <SelectItem value="enquiry_required" data-testid="avail-enquiry">Enquiry Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Availability Note</Label>
              <Input value={form.availability_note} onChange={set("availability_note")} data-testid="artist-avail-note-input" className={inputCls} placeholder="e.g. Booked till March" />
            </div>
            <div className="sm:col-span-2 flex gap-6">
              <label className="flex items-center gap-2 text-sm" data-testid="artist-featured-label">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="artist-featured-toggle" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm" data-testid="artist-active-label">
                <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="artist-active-toggle" /> Active (visible on site)
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label>Portfolio Images</Label>
              <div className="mt-2 flex flex-col gap-2">
                {form.portfolio.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={p.url} data-testid={`portfolio-url-${i}`} placeholder="Image URL"
                      onChange={(e) => setForm((f) => { const pf = [...f.portfolio]; pf[i] = { ...pf[i], url: e.target.value }; return { ...f, portfolio: pf }; })}
                      className={inputCls} />
                    <Input value={p.tag || ""} data-testid={`portfolio-tag-${i}`} placeholder="Tag"
                      onChange={(e) => setForm((f) => { const pf = [...f.portfolio]; pf[i] = { ...pf[i], tag: e.target.value }; return { ...f, portfolio: pf }; })}
                      className={`${inputCls} w-32`} />
                    <button type="button" data-testid={`portfolio-remove-${i}`}
                      onClick={() => setForm((f) => ({ ...f, portfolio: f.portfolio.filter((_, j) => j !== i) }))}
                      className="p-2 text-ivory/50 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" data-testid="portfolio-add-btn"
                  onClick={() => setForm((f) => ({ ...f, portfolio: [...f.portfolio, { url: "", tag: "" }] }))}
                  className="self-start text-xs font-semibold text-velvet hover:text-velvethover">
                  + Add portfolio image
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} data-testid="artist-save-btn"
                className="w-full rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : editing ? "Save Changes" : "Add Artist"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
